/**
 * InfinitSales - HandoffBanner
 *
 * Banner exibido no topo da conversa quando o agente transferiu o contato
 * para atendimento humano (handoff). Compartilhado pelos dois inboxes
 * (Evolution e WhatsApp Business) para a UI ficar idêntica.
 *
 * Conteúdo: aviso (com o NOME do responsável em negrito) + motivo + X para
 * fechar (não mostrar mais). Com a feature agent_resume_greeting (2026-09-15),
 * ganha o botão "Retomar agente" (onResume): os 4 shells já passam
 * handleResumeAgent, que chama PUT /chats/{chave}/resume-agent e, com a
 * flag no backend, dispara a mensagem de retomada configurada no agente.
 */
import React from 'react';
import { UserCheck, X, Play } from 'lucide-react';
import { getFeatureFlag } from '../hooks/useFeatureFlags';

const AMBER = '#f59e0b';

const HandoffBanner = ({ theme, colors, agentName, reason, targetName, onDismiss, onResume }) => {
  const isDark = theme === 'dark';
  const podeRetomar = Boolean(onResume) && getFeatureFlag('agent_resume_greeting');

  const bg = isDark
    ? 'linear-gradient(135deg, rgba(245,158,11,0.26) 0%, rgba(245,158,11,0.10) 100%)'
    : 'rgba(245,158,11,0.20)'; // mais opaco no claro
  const borderColor = isDark ? 'rgba(245,158,11,0.45)' : 'rgba(245,158,11,0.55)';
  const titleColor = isDark ? '#fcd34d' : '#92400e'; // dourado no dark, âmbar escuro no claro
  const agent = agentName || 'O agente';

  const styles = {
    bar: {
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '10px 16px',
      background: bg,
      borderBottom: `1px solid ${borderColor}`,
    },
    iconWrap: {
      width: 28, height: 28, borderRadius: 8, flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      backgroundColor: isDark ? 'rgba(245,158,11,0.22)' : 'rgba(245,158,11,0.28)',
      color: AMBER,
    },
    texts: { display: 'flex', flexDirection: 'column', gap: 1, flex: 1, minWidth: 0 },
    // título em peso NORMAL; só o nome do responsável fica em negrito.
    title: { fontSize: 12.5, fontWeight: 400, color: titleColor, lineHeight: 1.3 },
    reason: {
      fontSize: 11, color: colors.textSecondary,
      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
    },
  };

  return (
    <div style={styles.bar}>
      <div style={styles.iconWrap}>
        <UserCheck size={15} />
      </div>
      <div style={styles.texts}>
        <span style={styles.title}>
          {agent} transferiu o contato para{' '}
          <strong style={{ fontWeight: 700 }}>{targetName || 'um humano'}</strong>
          {' '}e não irá mais responder.
        </span>
        {reason && <span style={styles.reason}>Motivo: {reason}</span>}
      </div>
      {podeRetomar && (
        <button
          type="button"
          onClick={onResume}
          title="Devolver a conversa ao agente"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            padding: '5px 10px', borderRadius: 8, cursor: 'pointer', flexShrink: 0,
            border: `1px solid ${borderColor}`, background: 'transparent',
            color: titleColor, fontSize: 12, fontWeight: 600,
          }}
        >
          <Play size={12} />
          Retomar agente
        </button>
      )}
      {onDismiss && (
        <button
          onClick={onDismiss}
          title="Fechar (não mostrar mais este aviso)"
          style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: titleColor, padding: 4, flexShrink: 0,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <X size={16} />
        </button>
      )}
    </div>
  );
};

export default HandoffBanner;
